import React, { useEffect, useState, useCallback, useMemo } from "react";
import { http, currency } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Loader2, Calculator, Save, Package, Search, TrendingUp, TrendingDown, Info, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

// Cost calculator: analyze production costs per order or ad-hoc line,
// with editable per-product cost profile + business overhead/margin settings.
export default function AdminCosts() {
  const [tab, setTab] = useState("order"); // "order" | "profiles" | "settings"
  return (
    <div className="space-y-4" data-testid="admin-costs">
      <div className="flex items-center gap-2">
        <Calculator className="w-5 h-5" />
        <h2 className="font-display text-2xl">Calculadora de costos</h2>
        <div className="text-xs font-mono uppercase text-neutral-500 ml-auto">
          Costo real + precio sugerido + utilidad proyectada
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-neutral-200">
        {[
          { v: "order", l: "Analizar pedido" },
          { v: "profiles", l: "Costos por producto" },
          { v: "settings", l: "Parámetros del negocio" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`px-4 py-2 text-sm font-mono uppercase border-b-2 -mb-px ${
              tab === t.v ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"
            }`}
            data-testid={`admin-costs-tab-${t.v}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "order" && <OrderCostAnalyzer />}
      {tab === "profiles" && <ProductProfiles />}
      {tab === "settings" && <CostSettingsPanel />}
    </div>
  );
}


// ----- Global cost settings -----
function CostSettingsPanel() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/cost-settings");
      setValues(data);
    } catch {
      toast.error("No se pudieron cargar los parámetros");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/admin/cost-settings", values);
      toast.success("Parámetros guardados");
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!values) return <Loader2 className="w-5 h-5 animate-spin mt-6" />;

  const fields = [
    { k: "labor_rate_per_hour", label: "Costo mano de obra", unit: "$/hora", help: "Cuánto pagas al operario por hora" },
    { k: "machine_rate_per_hour", label: "Costo tiempo máquina", unit: "$/hora", help: "Depreciación + energía + consumo por hora de operación" },
    { k: "overhead_pct", label: "Gastos indirectos", unit: "% sobre COGS", help: "Renta, luz, papelería, admin, etc. como % del costo directo" },
    { k: "profit_margin_pct", label: "Margen de ganancia deseado", unit: "% sobre costo total", help: "Sobre COGS + indirectos — determina el precio sugerido" },
  ];

  return (
    <div className="space-y-3 max-w-2xl">
      <div className="text-xs text-neutral-500 italic">
        Estos parámetros aplican a todos los pedidos y productos. Modifícalos y guarda para ver el impacto inmediato en la calculadora.
      </div>
      {fields.map((f) => (
        <div key={f.k} className="border border-neutral-200 p-3">
          <label className="block text-[10px] font-mono uppercase text-neutral-500 mb-1">{f.label}</label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.01"
              min={0}
              value={values[f.k] ?? 0}
              onChange={(e) => setValues((v) => ({ ...v, [f.k]: parseFloat(e.target.value || 0) }))}
              className="rounded-none h-10 max-w-[180px]"
              data-testid={`admin-costs-setting-${f.k}`}
            />
            <span className="text-xs font-mono text-neutral-500">{f.unit}</span>
          </div>
          <div className="text-[10px] text-neutral-500 mt-1 flex items-start gap-1"><Info className="w-3 h-3 mt-px" />{f.help}</div>
        </div>
      ))}
      <Button onClick={save} disabled={saving} className="rounded-none bg-black hover:bg-neutral-800 text-white" data-testid="admin-costs-settings-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
        Guardar parámetros
      </Button>
    </div>
  );
}


// ----- Per-product cost profiles -----
function ProductProfiles() {
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/products");
      setProducts(data || []);
    } catch {
      toast.error("No se pudieron cargar los productos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const n = query.trim().toLowerCase();
    if (!n) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(n));
  }, [products, query]);

  if (selected) {
    return (
      <ProductProfileEditor
        product={selected}
        onBack={() => { setSelected(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar producto…"
          className="rounded-none h-10 pl-9 text-sm"
          data-testid="admin-costs-profile-search"
        />
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin mt-6" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {filtered.map((p) => {
            const cp = p.cost_profile || {};
            const configured = cp.substrate_cost || cp.labor_minutes || cp.machine_minutes;
            return (
              <button
                key={p.product_id}
                onClick={() => setSelected(p)}
                className={`text-left border p-3 flex gap-3 items-center hover:border-black transition-colors ${configured ? "border-emerald-500" : "border-neutral-200"}`}
                data-testid={`admin-costs-product-${p.product_id}`}
              >
                <div className="w-12 h-12 bg-neutral-100 shrink-0 overflow-hidden">
                  {p.base_image_url && <img src={p.base_image_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-[10px] font-mono text-neutral-500">
                    {currency(p.base_price || 0)} · {configured ? <span className="text-emerald-700">Perfil configurado</span> : <span className="text-neutral-400">Sin perfil</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ProductProfileEditor({ product, onBack }) {
  const initial = product.cost_profile || {};
  const [form, setForm] = useState({
    substrate_cost: initial.substrate_cost || 0,
    waste_pct: initial.waste_pct ?? 5,
    inks_cost: initial.inks_cost || 0,
    finishes_cost: initial.finishes_cost || 0,
    labor_minutes: initial.labor_minutes || 0,
    machine_minutes: initial.machine_minutes || 0,
    notes: initial.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k) => (
    <Input
      type="number"
      step="0.01"
      min={0}
      value={form[k] ?? 0}
      onChange={(e) => set(k, parseFloat(e.target.value || 0))}
      className="rounded-none h-10"
      data-testid={`admin-costs-profile-${k}`}
    />
  );

  const save = async () => {
    setSaving(true);
    try {
      await http.put(`/admin/products/${product.product_id}/cost-profile`, form);
      toast.success("Perfil de costos guardado");
      onBack();
    } catch {
      toast.error("No se pudo guardar el perfil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button size="sm" variant="outline" onClick={onBack} className="rounded-none">← Volver</Button>
        <div className="font-display text-xl">{product.name}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-[10px] font-mono uppercase text-neutral-500">Costo sustrato ($/pieza) {num("substrate_cost")}</label>
        <label className="text-[10px] font-mono uppercase text-neutral-500">Merma (% sobre materiales) {num("waste_pct")}</label>
        <label className="text-[10px] font-mono uppercase text-neutral-500">Tintas / consumibles ($/pieza) {num("inks_cost")}</label>
        <label className="text-[10px] font-mono uppercase text-neutral-500">Acabados / insumos ($/pieza) {num("finishes_cost")}</label>
        <label className="text-[10px] font-mono uppercase text-neutral-500">Mano de obra (min/pieza) {num("labor_minutes")}</label>
        <label className="text-[10px] font-mono uppercase text-neutral-500">Tiempo máquina (min/pieza) {num("machine_minutes")}</label>
      </div>
      <label className="text-[10px] font-mono uppercase text-neutral-500 block">
        Notas
        <textarea
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          rows={2}
          maxLength={500}
          className="w-full mt-1 px-3 py-2 text-sm border border-neutral-300 rounded-none"
          data-testid="admin-costs-profile-notes"
        />
      </label>
      <Button onClick={save} disabled={saving} className="rounded-none bg-black hover:bg-neutral-800 text-white" data-testid="admin-costs-profile-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
        Guardar perfil
      </Button>
    </div>
  );
}


// ----- Order-level cost analyzer -----
function OrderCostAnalyzer() {
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    http.get("/admin/orders").then(({ data }) => setOrders(data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const n = query.trim().toLowerCase();
    if (!n) return orders.slice(0, 100);
    return orders.filter(
      (o) =>
        (o.order_id || "").toLowerCase().includes(n) ||
        (o.user_name || "").toLowerCase().includes(n) ||
        (o.shipping?.name || "").toLowerCase().includes(n)
    );
  }, [orders, query]);

  const analyze = useCallback(async (orderId) => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await http.post("/admin/costs/calculate", { order_id: orderId });
      setResult(data);
      setSelected(orderId);
    } catch {
      toast.error("No se pudo calcular el costo");
    } finally {
      setLoading(false);
    }
  }, []);

  if (result) {
    return (
      <OrderCostResult
        result={result}
        onBack={() => { setResult(null); setSelected(null); }}
        onReload={() => analyze(selected)}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-neutral-500 italic">
        Elige un pedido para calcular su costo real, precio sugerido y utilidad proyectada.
        Los productos deben tener su perfil de costos configurado en la pestaña <em>Costos por producto</em>.
      </div>
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por ID, cliente…"
          className="rounded-none h-10 pl-9 text-sm"
          data-testid="admin-costs-order-search"
        />
      </div>
      {loading && <Loader2 className="w-5 h-5 animate-spin" />}
      <div className="border border-neutral-200 max-h-[520px] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-xs text-neutral-500 italic text-center">Sin pedidos</div>
        ) : (
          filtered.map((o) => (
            <button
              key={o.order_id}
              onClick={() => analyze(o.order_id)}
              className="w-full text-left px-3 py-2 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 flex items-center gap-3"
              data-testid={`admin-costs-pick-${o.order_id}`}
            >
              <Package className="w-4 h-4 text-neutral-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{o.user_name || o.shipping?.name || "—"}</div>
                <div className="text-[10px] font-mono text-neutral-500">{o.order_id} · {(o.items?.length || 0) + (o.custom_items?.length || 0)} ítem(s) · {currency(o.total)}</div>
              </div>
              <div className="text-[10px] font-mono uppercase text-neutral-500 shrink-0">{o.status}</div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}


function OrderCostResult({ result, onBack, onReload }) {
  const t = result.totals;
  const isProfit = t.profit >= 0;
  const goodMargin = t.profit_pct >= (result.settings.profit_margin_pct || 0);
  const missingProfile = (result.lines || []).some((l) => !l.has_profile && !l.is_custom);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={onBack} className="rounded-none">← Volver</Button>
        <div>
          <div className="text-[10px] font-mono uppercase text-neutral-500">Pedido</div>
          <div className="font-display text-lg leading-tight">{result.order?.customer || "—"} · <span className="font-mono text-xs text-neutral-500">{result.order?.order_id}</span></div>
        </div>
        <Button size="sm" variant="outline" onClick={onReload} className="rounded-none ml-auto"><RefreshCw className="w-3 h-3 mr-1" /> Recalcular</Button>
      </div>

      {missingProfile && (
        <div className="border-l-4 border-amber-500 bg-amber-50 p-2 text-xs text-amber-800">
          Algunos productos no tienen perfil de costos configurado. Aparecerán con costo 0 hasta que los configures en la pestaña <em>Costos por producto</em>.
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { label: "Materiales", value: currency(t.materials), sub: "sustrato + tintas + merma" },
          { label: "Acabados", value: currency(t.finishes), sub: "insumos por pieza" },
          { label: "Mano de obra", value: currency(t.labor), sub: "operario" },
          { label: "Tiempo máquina", value: currency(t.machine), sub: "operación equipo" },
        ].map((k) => (
          <div key={k.label} className="border border-neutral-200 p-3">
            <div className="text-[10px] font-mono uppercase text-neutral-500">{k.label}</div>
            <div className="font-display text-xl mt-0.5">{k.value}</div>
            <div className="text-[9px] text-neutral-500 italic">{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="border-2 border-black p-4">
          <div className="text-[10px] font-mono uppercase text-neutral-500">COGS (costo directo)</div>
          <div className="font-display text-3xl mt-0.5">{currency(t.cogs)}</div>
          <div className="text-xs text-neutral-500 mt-1">Materiales + acabados + mano de obra + tiempo máquina</div>
        </div>
        <div className="border-2 border-neutral-300 p-4">
          <div className="text-[10px] font-mono uppercase text-neutral-500">+ Indirectos ({result.settings.overhead_pct}%)</div>
          <div className="font-display text-3xl mt-0.5">{currency(t.overhead)}</div>
          <div className="text-xs text-neutral-500 mt-1">Renta, admin, servicios</div>
        </div>
        <div className="border-2 border-neutral-800 bg-neutral-50 p-4">
          <div className="text-[10px] font-mono uppercase text-neutral-500">Costo total real</div>
          <div className="font-display text-3xl mt-0.5">{currency(t.total_cost)}</div>
          <div className="text-xs text-neutral-500 mt-1">Punto de equilibrio (0% ganancia)</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="border-2 border-blue-600 bg-blue-50 p-4">
          <div className="text-[10px] font-mono uppercase text-blue-700">Precio sugerido (margen {result.settings.profit_margin_pct}%)</div>
          <div className="font-display text-3xl mt-0.5 text-blue-900">{currency(t.suggested_price)}</div>
          <div className="text-xs text-blue-800 mt-1">Precio que deberías cobrar para lograr tu margen objetivo.</div>
        </div>
        <div className="border-2 border-neutral-700 p-4">
          <div className="text-[10px] font-mono uppercase text-neutral-500">Precio de venta actual</div>
          <div className="font-display text-3xl mt-0.5">{currency(t.actual_sale)}</div>
          <div className="text-xs text-neutral-500 mt-1">Total del pedido cobrado al cliente.</div>
        </div>
        <div className={`border-2 p-4 ${isProfit && goodMargin ? "border-emerald-600 bg-emerald-50" : isProfit ? "border-amber-500 bg-amber-50" : "border-red-600 bg-red-50"}`}>
          <div className={`text-[10px] font-mono uppercase ${isProfit && goodMargin ? "text-emerald-700" : isProfit ? "text-amber-700" : "text-red-700"}`}>
            Utilidad bruta neta
          </div>
          <div className={`font-display text-3xl mt-0.5 flex items-center gap-2 ${isProfit ? (goodMargin ? "text-emerald-900" : "text-amber-900") : "text-red-900"}`}>
            {isProfit ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
            {currency(t.profit)}
          </div>
          <div className={`text-xs mt-1 ${isProfit && goodMargin ? "text-emerald-800" : isProfit ? "text-amber-800" : "text-red-800"}`}>
            {t.profit_pct}% de utilidad sobre venta
            {isProfit && !goodMargin && " · por debajo del margen objetivo"}
            {!isProfit && " · PÉRDIDA — revisa el perfil de costos o el precio cobrado"}
          </div>
        </div>
      </div>

      {/* Per-line breakdown */}
      <div className="border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left">
            <tr>
              <th className="p-2 text-[10px] font-mono uppercase text-neutral-500">Producto</th>
              <th className="p-2 text-[10px] font-mono uppercase text-neutral-500 text-right">Cant.</th>
              <th className="p-2 text-[10px] font-mono uppercase text-neutral-500 text-right">Costo unit.</th>
              <th className="p-2 text-[10px] font-mono uppercase text-neutral-500 text-right">Precio sug.</th>
              <th className="p-2 text-[10px] font-mono uppercase text-neutral-500 text-right">Precio real</th>
              <th className="p-2 text-[10px] font-mono uppercase text-neutral-500 text-right">Utilidad</th>
              <th className="p-2 text-[10px] font-mono uppercase text-neutral-500 text-right">%</th>
            </tr>
          </thead>
          <tbody>
            {(result.lines || []).map((L, i) => (
              <tr key={i} className="border-b border-neutral-100" data-testid={`admin-costs-line-${i}`}>
                <td className="p-2">
                  <div className="text-sm">{L.name}</div>
                  {L.variants && (L.variants.color || L.variants.size || L.variants.method) && (
                    <div className="text-[10px] font-mono text-neutral-500">
                      {[L.variants.color, L.variants.size, L.variants.method].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {!L.has_profile && !L.is_custom && (
                    <div className="text-[9px] text-amber-700 italic">sin perfil de costos</div>
                  )}
                  {L.is_custom && (
                    <div className="text-[9px] text-amber-700 italic">ítem cotización (no forma parte del catálogo)</div>
                  )}
                </td>
                <td className="p-2 text-right font-mono">{L.quantity}</td>
                <td className="p-2 text-right">{currency(L.total_cost_per_unit)}</td>
                <td className="p-2 text-right text-blue-800">{currency(L.suggested_price_per_unit)}</td>
                <td className="p-2 text-right">{currency(L.actual_sale_price_per_unit)}</td>
                <td className={`p-2 text-right ${L.line_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{currency(L.line_profit)}</td>
                <td className={`p-2 text-right font-mono ${L.line_profit_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>{L.line_profit_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
